import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Card,
  IconButton,
  Grid,
  IconButtonProps,
  styled,
  Collapse,
  Divider,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import { WsButton, WsColors, WsTypography } from 'wisestep-base';
interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}
const ExpandMore = styled((props: ExpandMoreProps) => {
  const { ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(90deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export interface AccordionDetailsCardProps {
  title: string;
  topChild: ReactElement;
  bottomChild: ReactElement;
  onToggle?: (check: boolean) => void;
  id?: string;
  hasData?: boolean;
  onEditClick?: () => void;
}
const AccordionDetailsCard = ({
  title,
  topChild,
  bottomChild,
  onToggle,
  id,
  hasData = true,
  onEditClick,
}: AccordionDetailsCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showMissingInfo, setShowMissingInfo] = useState(false);
  const onShowMore = () => {
    hasData && setExpanded(!expanded);
  };
  const toggleInfo = () => {
    setShowMissingInfo(!showMissingInfo);
    onToggle && onToggle(!showMissingInfo);
  };
  return (
    <Card
      sx={{
        width: '100%',
        border: 'solid 1px',
        borderColor: WsColors.BorderGrey,
        borderRadius: 1.5,
        boxShadow: 0,
      }}
      id={id}
    >
      <Grid
        component="div"
        container
        py={1}
        px={1}
        onClick={onShowMore}
        sx={{
          backgroundColor: expanded ? WsColors.TheBlue75 : WsColors.White,
          cursor: hasData ? 'pointer' : 'default',
        }}
      >
        <Grid item>
          <ExpandMore
            expand={expanded}
            aria-expanded={expanded}
            aria-label="show more"
            sx={{ padding: 0 }}
          >
            <PlayArrowIcon
              htmlColor={
                !hasData
                  ? WsColors.TertiaryShades200
                  : expanded
                  ? WsColors.TheBlues500
                  : WsColors.GreyBlack
              }
            />
          </ExpandMore>
        </Grid>
        <Grid
          item
          sm={7.5}
          md={7}
          lg={8.5}
          xl={8.5}
          display="flex"
          alignItems="center"
          alignContent="center"
        >
          <Typography
            color={WsColors.TertiaryShades900}
            sx={{
              overflow: 'hidden',
              display: 'block',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>
        </Grid>
        <Grid item xs={3} sm={3} md={4} lg={3} xl={3} display="flex" justifyContent="flex-end">
          {onToggle && (
            <FormControlLabel
              sx={{
                columnGap: 1,
              }}
              control={<Switch value={showMissingInfo} onChange={() => toggleInfo()} />}
              label="Show missing info"
              labelPlacement="start"
            />
          )}
          {onEditClick && (
            <WsButton size="small" variant="outlined" onClick={onEditClick}>
              Add/Edit
            </WsButton>
          )}
        </Grid>
      </Grid>
      <Divider></Divider>
      <Grid
        container
        p={1}
        spacing={1}
        sx={{ background: !hasData ? WsColors.TertiaryShades50 : '' }}
      >
        {!hasData && (
          <Grid ml={7.5}>
            <WsTypography>No Data To Show</WsTypography>
          </Grid>
        )}
        {hasData && (
          <Grid item xs={12} spacing={1} display="flex">
            {topChild}
          </Grid>
        )}
        {hasData && (
          <Grid item xs={12} spacing={1}>
            <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
              <Grid container>{bottomChild}</Grid>
            </Collapse>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default AccordionDetailsCard;
